import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, Sparkles } from 'lucide-react';
import { useStore } from '../store/useStore';
import { Analysis } from '../types';
import SearchBar from '../components/SearchBar';
import AnalysisCard from '../components/dashboard/AnalysisCard';
import AnalysisModal from '../components/dashboard/AnalysisModal';
import DeleteConfirmModal from '../components/dashboard/DeleteConfirmModal';

export default function Dashboard() {
  const { analyses, deleteAnalysis } = useStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAnalysis, setSelectedAnalysis] = useState<Analysis | null>(null);
  const [deleteConfirmAnalysis, setDeleteConfirmAnalysis] = useState<Analysis | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredAnalyses = useMemo(() => {
    return analyses.filter((analysis) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        analysis.fileName.toLowerCase().includes(searchLower) ||
        analysis.analysis.toLowerCase().includes(searchLower) ||
        new Date(analysis.createdAt).toLocaleDateString().includes(searchQuery)
      );
    });
  }, [analyses, searchQuery]);

  const handleDelete = async (analysis: Analysis) => {
    setDeleteConfirmAnalysis(analysis);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmAnalysis) return;

    try {
      setIsDeleting(true);
      setError(null);
      await deleteAnalysis(deleteConfirmAnalysis.id);
      setDeleteConfirmAnalysis(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 dark:text-navy-50">
            Parecer automatizado
          </h1>
          <p className="text-navy-500 dark:text-navy-400 text-sm mt-1">
            Gerencie seus pareceres jurídicos automatizados
          </p>
        </div>
        <Link
          to="/new"
          className="inline-flex items-center px-5 py-2.5 btn-accent rounded-lg shadow-gold hover:shadow-gold-lg transition-all duration-200"
        >
          <Plus className="h-5 w-5 mr-2" />
          Novo Parecer
        </Link>
      </div>

      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Buscar por nome do arquivo ou conteúdo..."
      />

      {error && (
        <div className="p-4 bg-error-500/10 border border-error-500/30 text-error-500 dark:text-error-400 rounded-lg">
          {error}
        </div>
      )}

      {filteredAnalyses.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-navy-900 rounded-xl border border-navy-200 dark:border-navy-700">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-navy-100 dark:bg-navy-800 rounded-full mb-4">
            <FileText className="h-8 w-8 text-navy-400 dark:text-navy-500" />
          </div>
          <h3 className="text-lg font-semibold text-navy-900 dark:text-navy-100 mb-2">
            Nenhum parecer encontrado
          </h3>
          <p className="text-navy-500 dark:text-navy-400 mb-6">
            Comece criando um novo parecer jurídico com IA.
          </p>
          <Link
            to="/new"
            className="inline-flex items-center px-5 py-2.5 btn-accent rounded-lg"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            Criar Primeiro Parecer
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredAnalyses.map((analysis) => (
            <AnalysisCard
              key={analysis.id}
              analysis={analysis}
              onView={setSelectedAnalysis}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <AnalysisModal
        analysis={selectedAnalysis}
        onClose={() => setSelectedAnalysis(null)}
      />

      <DeleteConfirmModal
        analysis={deleteConfirmAnalysis}
        isDeleting={isDeleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteConfirmAnalysis(null)}
      />
    </div>
  );
}
